import React from 'react';
import { RouteProps } from 'react-router';
import {
	MY_ACCOUNT_ADDRESS_BOOK_CREATE_URL,
	MY_ACCOUNT_ADDRESS_BOOK_EDIT_URL,
	MY_ACCOUNT_ADDRESS_BOOK_URL,
	MY_ACCOUNT_PATH_PREFIX,
} from './routes.roc';

//#region TapeCase
import { OrganizationUIComponents } from 'features/commerce/organization/OrganizationUIComponents';
import {
	ORGANIZATION_USERS_URL,
	ORGANIZATION_SHIPTOS_URL,
	ORGANIZATION_ORDERS_URL,
	ORGANIZATION_USERS_CREATE_URL,
} from 'features/commerce/organization/routes';
import { ServerData } from 'services/ServerData';
//#endregion TapeCase

//#region URLs
export const MY_ACCOUNT_ORDERS_URL = `${MY_ACCOUNT_PATH_PREFIX}/orders`;
export const MY_ACCOUNT_STORE_URL = `${MY_ACCOUNT_PATH_PREFIX}/store`;
export const MY_ACCOUNT_SAVED_FOR_LATER_URL = `${MY_ACCOUNT_PATH_PREFIX}/saved-for-later`;
export const MY_ACCOUNT_SAVED_CARTS_URL = `${MY_ACCOUNT_PATH_PREFIX}/saved-carts`;
export const MY_ACCOUNT_BUY_AGAIN_URL = `${MY_ACCOUNT_PATH_PREFIX}/buy-again`;
export const MY_ACCOUNT_SHOPPING_LISTS_URL = `${MY_ACCOUNT_PATH_PREFIX}/shopping-lists`;
export const MY_ACCOUNT_ORGANIZATION_REVIEW_URL = `${MY_ACCOUNT_PATH_PREFIX}/organization-review`;
export const MY_ACCOUNT_ORGANIZATION_SELECTOR_URL = `${MY_ACCOUNT_PATH_PREFIX}/organization-selector`;
export const MY_ACCOUNT_SHOPPING_LISTS_CREATE_URL = `${MY_ACCOUNT_SHOPPING_LISTS_URL}/create`;
//#endregion URLs

//#region TapeCase Customization
export const MY_ACCOUNT_USER_SHIPPING_ACCOUNT_URL = `${MY_ACCOUNT_PATH_PREFIX}/shipping-accounts`;
export const MY_ACCOUNT_USER_SHIPPING_ACCOUNT_CREATE_URL = `${MY_ACCOUNT_USER_SHIPPING_ACCOUNT_URL}/create`;
export const MY_ACCOUNT_USER_SHIPPING_ACCOUNT_EDIT_URL = `${MY_ACCOUNT_USER_SHIPPING_ACCOUNT_URL}/edit`;
//#endregion TapeCase

//#region Components
const OrdersComponent = React.lazy(
	() => import(/* webpackChunkName: "my-account-orders" */ '../commerce/my-account/orders/OrderListing'),
);

const OrderDetailsComponent = React.lazy(
	() =>
		import(
			/* webpackChunkName: "my-account-order-details" */ '../commerce/my-account/orders/MyAccountOrderDetails'
		),
);

const StoreComponent = React.lazy(
	() => import(/* webpackChunkName: "my-account-store" */ '../commerce/my-account/store/Store'),
);

const AddressBookComponent = React.lazy(
	() => import(/* webpackChunkName: "my-account-address-book" */ '../address-book/AddressBook'),
);

const AddressBookEditComponent = React.lazy(
	() => import(/* webpackChunkName: "my-account-address-book-edit" */ '../address-book/AddressBookEdit'),
);

const SavedForLaterComponent = React.lazy(
	() =>
		import(
			/* webpackChunkName: "my-account-saved-for-later" */ '../commerce/my-account/saved-for-later/SavedForLaterListingWrapper'
		),
);

const SavedCartsComponent = React.lazy(
	() =>
		import(
			/* webpackChunkName: "my-account-saved-carts" */ '../commerce/my-account/saved-carts/SavedCartsListingWrapper'
		),
);

const BuyAgainComponent = React.lazy(
	() => import(/* webpackChunkName: "buy-again" */ '../commerce/my-account/buy-again/BuyAgain'),
);

const AccountOverviewComponent = React.lazy(
	() =>
		import(
			/* webpackChunkName: "my-account-account-overview" */ '../commerce/my-account/account-overview/AccountOverview'
		),
);

const ShoppingListsComponent = React.lazy(
	() =>
		import(
			/* webpackChunkName: "my-account-shopping-lists" */ '../commerce/my-account/shopping-lists/ShoppingListListing'
		),
);

const ShoppingListsCreateComponent = React.lazy(
	() =>
		import(
			/* webpackChunkName: "my-account-shopping-lists-create" */ '../commerce/my-account/shopping-lists/ShoppingListsCreate'
		),
);

const ShoppingListDetailsComponent = React.lazy(
	() =>
		import(
			/* webpackChunkName: "my-account-shopping-list-details" */ '../commerce/my-account/shopping-lists/MyAccountShoppingListDetails'
		),
);

const OrganizationReviewComponent = React.lazy(
	() =>
		import(
			/* webpackChunkName: "my-account-organization-approval" */ '../commerce/my-account/organization/OrganizationReview'
		),
);

const OrganizationSelectorComponent = React.lazy(
	() =>
		import(
			/* webpackChunkName: "my-account-organization-selector" */ '../commerce/my-account/organization/OrganizationSelector'
		),
);

const OrganizationOrderDetailsComponent = React.lazy(() => {
	const orderDetailsComponent =
		ServerData?.OrderDetailsUIComponent && ServerData?.OrderDetailsUIComponent in OrganizationUIComponents
			? ServerData.OrderDetailsUIComponent
			: OrganizationUIComponents.DefaultOrganizationOrderDetails;
	return import(
		/* webpackChunkName: "organization-order-details" */ `../commerce/organization/orders/${orderDetailsComponent}`
	);
});

const OrganizationShiptosComponent = React.lazy(
	() => import(/* webpackChunkName: "ship-to" */ '../commerce/organization/ship-to/OrganizationShipTos'),
);

const OrganizationUsersComponent = React.lazy(
	() => import(/* webpackChunkName: "org-users" */ '../commerce/organization/users/OrganizationUsers'),
);

const OrganizationOrdersComponent = React.lazy(
	() => import(/* webpackChunkName: "org-orders" */ '../commerce/organization/orders/OrganizationOrders'),
);

const OrganizationUsersCreateComponent = React.lazy(
	() => import(/* webpackChunkName: "org-users-create" */ '../commerce/organization/users/OrganizationUsersCreate'),
);

const UserShippingAccountsComponent = React.lazy(
	() =>
		import(
			/* webpackChunkName: "my-account-address-book" */ '../commerce/user-shipping-account/UserShippingAccounts'
		),
);

const UserShippingAccountsEditComponent = React.lazy(
	() =>
		import(
			/* webpackChunkName: "my-account-address-book-edit" */ '../commerce/user-shipping-account/UserShippingAccountsEdit'
		),
);
//#endregion
//#endregion Components

//#region Routes
const ordersRoute = {
	exact: true,
	path: MY_ACCOUNT_ORDERS_URL,
	component: OrdersComponent,
};

const orderDetailsRoute = {
	exact: true,
	path: `${MY_ACCOUNT_ORDERS_URL}/:orderNo`,
	component: OrderDetailsComponent,
};

const storeRoute = {
	exact: true,
	path: MY_ACCOUNT_STORE_URL,
	component: StoreComponent,
};

const addressBookRoute = {
	exact: true,
	path: MY_ACCOUNT_ADDRESS_BOOK_URL,
	component: AddressBookComponent,
};

const addressBookCreateRoute = {
	exact: true,
	path: MY_ACCOUNT_ADDRESS_BOOK_CREATE_URL,
	component: AddressBookEditComponent,
};

const addressBookEditRoute = {
	exact: true,
	path: `${MY_ACCOUNT_ADDRESS_BOOK_EDIT_URL}/:addressId`,
	component: AddressBookEditComponent,
};

const savedForLaterRoute = {
	exact: true,
	path: MY_ACCOUNT_SAVED_FOR_LATER_URL,
	component: SavedForLaterComponent,
};

const savedCartsRoute = {
	exect: true,
	path: MY_ACCOUNT_SAVED_CARTS_URL,
	component: SavedCartsComponent,
};

const buyAgainRoute = {
	exact: true,
	path: MY_ACCOUNT_BUY_AGAIN_URL,
	component: BuyAgainComponent,
};

const shoppingListsRoute = {
	exact: true,
	path: MY_ACCOUNT_SHOPPING_LISTS_URL,
	component: ShoppingListsComponent,
};

const shoppingListsCreateRoute = {
	exact: true,
	path: MY_ACCOUNT_SHOPPING_LISTS_CREATE_URL,
	component: ShoppingListsCreateComponent,
};

const shoppingListDetailsRoute = {
	exact: true,
	path: `${MY_ACCOUNT_SHOPPING_LISTS_URL}/:shoppingListId`,
	component: ShoppingListDetailsComponent,
};

const defaultRoute = {
	exact: false,
	path: '*',
	component: AccountOverviewComponent,
};

const organizationReviewRoute = {
	exact: true,
	path: MY_ACCOUNT_ORGANIZATION_REVIEW_URL,
	component: OrganizationReviewComponent,
};

const organizationSelectorRoute = {
	exact: true,
	path: MY_ACCOUNT_ORGANIZATION_SELECTOR_URL,
	component: OrganizationSelectorComponent,
};
//#region TapeCase

const OrganizationUsersRoute = {
	exact: true,
	path: ORGANIZATION_USERS_URL,
	component: OrganizationUsersComponent,
};

const OrganizationShipTosRoute = {
	exact: true,
	path: ORGANIZATION_SHIPTOS_URL,
	component: OrganizationShiptosComponent,
};

const OrganizationOrdersRoute = {
	exact: true,
	path: ORGANIZATION_ORDERS_URL,
	component: OrganizationOrdersComponent,
};

const OrganizationCreateUserRoute = {
	exact: true,
	path: ORGANIZATION_USERS_CREATE_URL,
	component: OrganizationUsersCreateComponent,
};

const OrganizationOrderDetailsRoute = {
	exact: true,
	path: ORGANIZATION_ORDERS_URL,
	component: OrganizationOrderDetailsComponent,
};
//#endregion TapeCase
//#endregion Routes

//#region TapeCase Routes
const userShippingAccountsRoute = {
	exact: true,
	path: MY_ACCOUNT_USER_SHIPPING_ACCOUNT_URL,
	component: UserShippingAccountsComponent,
};

const userShippingAccountsCreateRoute = {
	exact: true,
	path: MY_ACCOUNT_USER_SHIPPING_ACCOUNT_CREATE_URL,
	component: UserShippingAccountsEditComponent,
};

const userShippingAccountsEditRoute = {
	exact: true,
	path: `${MY_ACCOUNT_USER_SHIPPING_ACCOUNT_EDIT_URL}/:shippingAccountId`,
	component: UserShippingAccountsEditComponent,
};
//#endregion

export const commerceRoutes: RouteProps[] = [
	ordersRoute,
	orderDetailsRoute,
	storeRoute,
	addressBookRoute,
	addressBookCreateRoute,
	addressBookEditRoute,
	savedForLaterRoute,
	savedCartsRoute,
	buyAgainRoute,
	shoppingListsRoute,
	shoppingListsCreateRoute,
	shoppingListDetailsRoute,
	organizationReviewRoute,
	organizationSelectorRoute,
	//#region TapeCase
	userShippingAccountsRoute,
	userShippingAccountsCreateRoute,
	userShippingAccountsEditRoute,
	OrganizationCreateUserRoute,
	OrganizationUsersRoute,
	OrganizationShipTosRoute,
	OrganizationOrdersRoute,
	OrganizationOrderDetailsRoute,
	//#endregion
	defaultRoute,
];
